import {
  Activity,
  Camera,
  EscalateToIntrusionWithDescriptionOutdoorActivityType,
  FaceDetectionType,
  Incomplete2WAOutdoorActivityResultReason,
  Incomplete2WAOutdoorActivityResultType,
  MonitoringSpecialistTriageOutdoorActivityType,
  MotionDetectionType,
  No2WAOutdoorActivityResultReason,
  No2WAOutdoorActivityResultType,
  OutdoorEngagement,
  OutdoorEngagementResolutionCategory,
  OutdoorEngagementResult,
  PersonDetectionType,
  TwoWayAudioInteractionOutdoorActivityType
} from '../../../../api/models'
import { camelCaseToSplitWords } from '../activityUtils'
import { findCameraName } from '../ertUtils'
import {
  clampToRange,
  countMatchedFamiliarFaces,
  findActivityOfType,
  findFamiliarFaceReview,
  findFirstDetection,
  getActivityTimestamp,
  getClearedByCustomerTimestamp,
  getFailedEndingActivity,
  getFailedEndingKind,
  getLastActivity,
  getTerminalTimestamp,
  hasClearingActivity,
  MainCanvasLiveStreamFacts,
  neverReachedSpecialist,
  SectionResult,
  SectionText,
  TimelineFragmentKind,
  wasAutoCleared,
  wasClearedByAI
} from './customerCareTimelineUtils'

interface DetectionData {
  detectionType?: PersonDetectionType | FaceDetectionType | MotionDetectionType
  cameraName?: string
  time?: string
}

function detectionFragment(data: DetectionData): SectionResult {
  if (!data.detectionType || !data.cameraName || !data.time) return null
  const isMotionOnly = data.detectionType === MotionDetectionType.Motion
  return {
    header: `${data.detectionType} detected by '${data.cameraName}' camera`,
    subtext: `The camera detected ${isMotionOnly ? 'motion' : 'a person'} and created an event.`,
    time: data.time,
    kind: TimelineFragmentKind.Detection
  }
}

export const TRIAGE_HEADER = 'Reviewed by an Outdoor Monitoring Agent'

interface TriageData {
  activity?: Activity
}

function triageFragment(data: TriageData): SectionResult {
  if (!data.activity) return null
  return {
    header: TRIAGE_HEADER,
    subtext: 'An agent picked up and reviewed the event.',
    time: getActivityTimestamp(data.activity),
    kind: TimelineFragmentKind.Triage
  }
}

interface FamiliarFaceData {
  activity?: Activity
  matchedFaceCount: number
}

export function familiarFaceHeader(matchedFaceCount: number): string {
  const faceLabel = matchedFaceCount === 1 ? 'familiar face' : 'familiar faces'
  return `Agent matched face detections to ${matchedFaceCount} ${faceLabel}`
}

function familiarFaceFragment(data: FamiliarFaceData): SectionResult {
  if (!data.activity) return null
  return {
    // Chart lists no subtext for this row - it's Figma-only static copy, not derived from data.
    header: familiarFaceHeader(data.matchedFaceCount),
    time: getActivityTimestamp(data.activity),
    kind: TimelineFragmentKind.FamiliarFace
  }
}

interface LiveStreamViewData {
  numberOfCamerasViewedLive: number
  firstLiveViewTimestamp?: string
}

function liveStreamViewFragment(data: LiveStreamViewData): SectionResult {
  if (data.numberOfCamerasViewedLive <= 0 || !data.firstLiveViewTimestamp) return null
  const deviceLabel = data.numberOfCamerasViewedLive === 1 ? 'device' : 'devices'
  return {
    header: `Agent viewed live streams from ${data.numberOfCamerasViewedLive} ${deviceLabel}`,
    time: data.firstLiveViewTimestamp,
    kind: TimelineFragmentKind.LiveStreamView
  }
}

interface TwoWayAudioData {
  hasTwoWayAudioActivity: boolean
  activity?: Activity
  statedPurpose?: string
  audioQuality?: string
}

export const TWO_WAY_AUDIO_HEADER = 'Agent used two-way audio'

function twoWayAudioFragment(data: TwoWayAudioData): SectionResult {
  if (!data.hasTwoWayAudioActivity || !data.activity) return null
  return {
    header: TWO_WAY_AUDIO_HEADER,
    subtext: [
      { text: 'The agent interacted with the person on the camera.' },
      { label: 'Stated Purpose:', text: data.statedPurpose || 'None given' },
      { label: 'Audio Quality:', text: data.audioQuality ?? '—' }
    ],
    time: getActivityTimestamp(data.activity),
    kind: TimelineFragmentKind.TwoWayAudio
  }
}

interface ClearedData {
  time?: string
  resolutionCategory?: OutdoorEngagementResolutionCategory
  wasAutoCleared: boolean
}

function clearedFragment(data: ClearedData): SectionResult {
  if (!data.time) return null
  const resolutionCategory = camelCaseToSplitWords(data.resolutionCategory)
  return {
    header: 'Cleared event',
    subtext: data.wasAutoCleared ? `Cleared automatically because it was ${resolutionCategory}` : `Cleared as "${resolutionCategory}".`,
    time: data.time,
    kind: TimelineFragmentKind.Cleared
  }
}

type LiveStreamFailureReason = 'Failed' | 'LivestreamDropped'

// The engagement's own recorded two-way-audio outcome is one of the two facts this row may report from, the
// other being the rrweb recording (see computeMainCanvasLiveStreamFacts in customerCareTimelineUtils.ts) -
// see the note on computeLiveStreamFacts (in customerCareTimelineUtils.ts) for why videoAccessError can't
// be used here.
function getRecordedLiveStreamFailureReason(
  twoWayAudioInteraction: OutdoorEngagementResult['twoWayAudioInteraction']
): LiveStreamFailureReason | undefined {
  if (
    twoWayAudioInteraction?.type === Incomplete2WAOutdoorActivityResultType.IncompleteTwoWayAudioInteraction &&
    twoWayAudioInteraction.reason === Incomplete2WAOutdoorActivityResultReason.LivestreamDropped
  ) {
    return 'LivestreamDropped'
  }
  if (
    twoWayAudioInteraction?.type === No2WAOutdoorActivityResultType.NoTwoWayAudioInteraction &&
    twoWayAudioInteraction.reason === No2WAOutdoorActivityResultReason.Failed
  ) {
    return 'Failed'
  }
  return undefined
}

export const LIVE_STREAM_FAILURE_SUBTEXT: Record<LiveStreamFailureReason, string> = {
  Failed: "The live connection to the camera failed, so the agent couldn't attempt two-way audio. No audio was sent.",
  LivestreamDropped: 'The live stream dropped during the interaction, cutting off two-way audio partway through.'
}

// Used when only the recording shows the failure - a live stream that reached the main canvas with nothing to watch.
export const LIVE_STREAM_FAILED_SUBTEXT = "The live connection to the camera failed, so the agent couldn't watch the live stream."

export const LIVE_STREAM_FAILED_HEADER = 'Live stream failed'

interface LiveStreamFailedData {
  // From the engagement's two-way-audio result
  recordedReason?: LiveStreamFailureReason
  recordedTime?: string
  // From a live stream on the main video canvas that had nothing to watch (rrweb)
  rrwebFailureTime?: string
}

// Either source is enough to show the row. When both apply, the 2WA result's more specific copy is used, with
// the time the recording says the stream failed. Without either, there's no fallback timestamp (e.g.
// triage/terminal) - FR-8 rules out substituting a different recorded fact just to still show the row.
function liveStreamFailedFragment(data: LiveStreamFailedData): SectionResult {
  const hasRecordedFailure = !!data.recordedReason && !!data.recordedTime
  const time = data.rrwebFailureTime ?? (hasRecordedFailure ? data.recordedTime : undefined)
  if (!time) return null
  return {
    header: LIVE_STREAM_FAILED_HEADER,
    subtext: hasRecordedFailure && data.recordedReason ? LIVE_STREAM_FAILURE_SUBTEXT[data.recordedReason] : LIVE_STREAM_FAILED_SUBTEXT,
    time,
    kind: TimelineFragmentKind.LiveStreamFailed
  }
}

export const ESCALATED_HEADER = 'Agent escalated for potential dispatch'

interface EscalatedData {
  activity?: Activity
}

function escalatedFragment(data: EscalatedData): SectionResult {
  if (!data.activity) return null
  return {
    header: ESCALATED_HEADER,
    subtext: 'The agent escalated with a description. What happens next is recorded separately.',
    time: getActivityTimestamp(data.activity),
    kind: TimelineFragmentKind.Escalated
  }
}

interface ClearedByAIData {
  wasClearedByAI: boolean
  resolutionTimestamp?: string
}

function clearedByAIFragment(data: ClearedByAIData): SectionResult {
  if (!data.wasClearedByAI || !data.resolutionTimestamp) return null
  return {
    header: 'Reviewed by Camera AI',
    subtext: 'Camera AI reviewed the footage and cleared it without an agent.',
    time: data.resolutionTimestamp,
    kind: TimelineFragmentKind.ClearedByAI
  }
}

export const BOUNCED_HEADER = 'The event bounced'
export const DROPPED_HEADER = 'The event dropped'
export const ABANDONED_HEADER = 'The event was abandoned'

const FAILED_ENDING_HEADERS: Partial<Record<TimelineFragmentKind, string>> = {
  [TimelineFragmentKind.Bounced]: BOUNCED_HEADER,
  [TimelineFragmentKind.Dropped]: DROPPED_HEADER,
  [TimelineFragmentKind.Abandoned]: ABANDONED_HEADER
}

interface FailedEndingData {
  activity?: Activity
  kind?: TimelineFragmentKind
}

function failedEndingFragment(data: FailedEndingData): SectionResult {
  const header = data.kind && FAILED_ENDING_HEADERS[data.kind]
  if (!data.activity || !data.kind || !header) return null
  return { header, time: getActivityTimestamp(data.activity), kind: data.kind }
}

export function buildTimelineFragments(
  engagement: OutdoorEngagement,
  activities: Activity[],
  cameras: Camera[] | undefined,
  liveStream: MainCanvasLiveStreamFacts | undefined
): SectionText[] {
  const firstDetection = findFirstDetection(engagement)
  const twoWayAudioInteraction = engagement.result?.twoWayAudioInteraction
  const twoWayAudioActivity = findActivityOfType(activities, TwoWayAudioInteractionOutdoorActivityType.TwoWayAudioInteraction)
  const triageActivity = findActivityOfType(activities, MonitoringSpecialistTriageOutdoorActivityType.MonitoringSpecialistTriage)
  const triageTimestamp = triageActivity && getActivityTimestamp(triageActivity)
  const terminalTimestamp = getTerminalTimestamp(engagement, activities)
  const lastActivity = getLastActivity(activities)
  const clearedByCustomerTimestamp = getClearedByCustomerTimestamp(engagement, activities)
  const clearedTime = lastActivity && hasClearingActivity(activities) ? getActivityTimestamp(lastActivity) : clearedByCustomerTimestamp

  const familiarFaceActivity = findFamiliarFaceReview(activities)

  const clampedLiveStream: MainCanvasLiveStreamFacts | undefined = liveStream && {
    ...liveStream,
    firstLiveViewTimestamp: clampToRange(liveStream.firstLiveViewTimestamp, triageTimestamp, terminalTimestamp),
    firstLiveFailureTimestamp: clampToRange(liveStream.firstLiveFailureTimestamp, triageTimestamp, terminalTimestamp)
  }

  // Cleared/Escalated/ClearedByAI/failed endings are pushed last so that, on an exact-minute tie against a clamped
  // rrweb-derived row above, orderTimelineFragments's stable sort still keeps the terminal outcome last.
  const fragments: SectionResult[] = [
    detectionFragment({
      detectionType: firstDetection?.type,
      cameraName: findCameraName(engagement.triggeringDevice.id, cameras),
      time: firstDetection?.time
    }),
    triageFragment({ activity: neverReachedSpecialist(engagement, activities) ? undefined : triageActivity }),
    clampedLiveStream ? liveStreamViewFragment(clampedLiveStream) : null,
    familiarFaceFragment({
      activity: familiarFaceActivity,
      matchedFaceCount: countMatchedFamiliarFaces(familiarFaceActivity)
    }),
    twoWayAudioFragment({
      hasTwoWayAudioActivity: !!twoWayAudioInteraction && twoWayAudioInteraction.type !== No2WAOutdoorActivityResultType.NoTwoWayAudioInteraction,
      activity: twoWayAudioActivity,
      statedPurpose: twoWayAudioInteraction?.statedPurpose,
      audioQuality: twoWayAudioInteraction?.audioQuality
    }),
    liveStreamFailedFragment({
      recordedReason: getRecordedLiveStreamFailureReason(twoWayAudioInteraction),
      recordedTime: twoWayAudioActivity && getActivityTimestamp(twoWayAudioActivity),
      rrwebFailureTime: clampedLiveStream?.firstLiveFailureTimestamp
    }),
    clearedFragment({
      time: clearedTime,
      resolutionCategory: engagement.result?.resolution?.category,
      wasAutoCleared: wasAutoCleared(activities) || !!clearedByCustomerTimestamp
    }),
    clearedByAIFragment({ wasClearedByAI: wasClearedByAI(engagement), resolutionTimestamp: engagement.result?.resolution?.timestamp }),
    escalatedFragment({
      activity: findActivityOfType(activities, EscalateToIntrusionWithDescriptionOutdoorActivityType.EscalateToIntrusionWithDescription)
    }),
    failedEndingFragment({ activity: getFailedEndingActivity(engagement, activities), kind: getFailedEndingKind(engagement, activities) })
  ]

  return fragments.filter((fragment): fragment is SectionText => fragment !== null)
}
